import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, Shield, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { NAV_LINKS, APP_LINKS } from "@/data/content";
import { scrollToId } from "@/lib/lenis";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const goSection = (section) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: section } });
    } else {
      scrollToId(section);
    }
  };

  const linkCls =
    "relative whitespace-nowrap text-[13px] font-semibold uppercase tracking-[0.14em] text-foreground/70 transition-colors duration-300 hover:text-gold";

  const renderLink = (link) => {
    const isActive = link.to === location.pathname;
    return (
      <a
        key={link.label}
        href={link.to}
        data-testid={`nav-link-${link.to.replace("/", "") || "accueil"}`}
        aria-current={isActive ? "page" : undefined}
        className={`${linkCls} ${isActive ? "text-gold" : ""}`}
      >
        {link.label}
      </a>
    );
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || open ? "glass border-b border-gold/10 shadow-[0_8px_40px_rgba(0,0,0,0.35)]" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="/" data-testid="navbar-logo-link" aria-label="Motards Business Club 84 — retour à l'accueil">
          <Logo size={38} />
        </a>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigation principale">
          {/* La confidentialité a son onglet dédié (icône bouclier) dans le cluster de droite sur desktop, et un onglet complet dans le menu mobile */}
          {NAV_LINKS.filter((l) => l.to !== "/confidentialite").map(renderLink)}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="/confidentialite"
            data-testid="nav-confidentialite-desktop"
            aria-label="Politique de confidentialité"
            title="Politique de confidentialité"
            className="hidden h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold transition-colors duration-300 hover:bg-gold/10 lg:inline-flex"
          >
            <Shield className="h-[18px] w-[18px]" aria-hidden="true" />
          </a>
          <ThemeToggle />
          <a
            href={APP_LINKS.joinClub}
            data-testid="navbar-join-cta"
            className="btn-sheen hidden items-center rounded-full bg-gradient-to-r from-gold-deep via-gold to-gold-hover px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em] text-night shadow-[0_4px_24px_rgba(212,175,55,0.35)] transition-transform duration-300 hover:scale-[1.03] sm:inline-flex"
          >
            Rejoindre le club
          </a>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="navbar-mobile-toggle"
            aria-label={open ? "Fermer le menu de navigation" : "Ouvrir le menu de navigation"}
            aria-expanded={open}
            aria-controls="menu-mobile"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="menu-mobile"
            aria-label="Navigation mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="glass overflow-hidden border-b border-gold/10 lg:hidden"
            data-testid="navbar-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 pb-6 pt-2">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${link.to.replace("/", "") || "accueil"}`}
                  aria-current={link.to === location.pathname ? "page" : undefined}
                  className={`block w-full py-3 font-heading text-lg font-bold uppercase tracking-wide hover:text-gold ${link.to === location.pathname ? "text-gold" : "text-foreground/85"}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href={APP_LINKS.joinClub}
                data-testid="mobile-join-cta"
                className="btn-sheen mt-3 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-gold-deep via-gold to-gold-hover px-5 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-night"
              >
                Rejoindre le club
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
